import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

export interface SettingsNestedItem {
  id: string;
  label: string;
  path: string;
  hidden?: boolean;
  disabled?: boolean;
  onSelect?: (path: string) => void;
}

interface SettingsNestedGroupProps {
  label: string;
  items: SettingsNestedItem[];
  currentPath?: string;
  defaultExpanded?: boolean;
  disabled?: boolean;
}

const Group = styled.nav`
  width: min(100%, 300px);
  color: ${props => props.theme.colors.black};
`;

const Disclosure = styled.button`
  display: flex;
  width: 100%;
  min-height: 38px;
  align-items: center;
  justify-content: space-between;
  border: 0;
  border-radius: 4px;
  padding: 8px 10px;
  background: ${props => props.theme.colors.midNightBlue};
  color: white;
  font-weight: 700;
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid ${props => props.theme.colors.skyBlue};
    outline-offset: 2px;
  }
`;

const ItemList = styled.ul`
  list-style: none;
  padding: 4px 0 0 14px;
  margin: 0;
`;

const ItemLink = styled.a<{ current: boolean }>`
  display: block;
  padding: 8px 10px;
  border-left: 3px solid
    ${props => (props.current ? props.theme.colors.green : "transparent")};
  background: ${props =>
    props.current ? props.theme.colors.softGreen : "transparent"};
  color: ${props => props.theme.colors.black};
  font-weight: ${props => (props.current ? 700 : 400)};
  text-decoration: none;

  &:focus-visible {
    outline: 3px solid ${props => props.theme.colors.darkBlue};
    outline-offset: 1px;
  }

  &[aria-disabled="true"] {
    color: ${props => props.theme.colors.darkGrey};
    cursor: not-allowed;
  }
`;

export const SettingsNestedGroup = ({
  label,
  items,
  currentPath,
  defaultExpanded = false,
  disabled = false
}: SettingsNestedGroupProps) => {
  const hasCurrentDescendant = useMemo(
    () => items.some(item => !item.hidden && item.path === currentPath),
    [currentPath, items]
  );
  const [expanded, setExpanded] = useState(
    defaultExpanded || hasCurrentDescendant
  );
  const groupId = `settings-group-${label.replace(/\s+/g, "-")}`;

  useEffect(() => {
    if (hasCurrentDescendant) {
      setExpanded(true);
    }
  }, [hasCurrentDescendant]);

  return (
    <Group aria-label={`${label} 하위 메뉴`}>
      <Disclosure
        type="button"
        aria-expanded={expanded}
        aria-controls={groupId}
        disabled={disabled}
        onClick={() => setExpanded(value => !value)}
      >
        <span>{label}</span>
        <span aria-hidden="true">{expanded ? "▾" : "▸"}</span>
      </Disclosure>
      {expanded && (
        <ItemList id={groupId}>
          {items
            .filter(item => !item.hidden)
            .map(item => {
              const current = item.path === currentPath;
              return (
                <li key={item.id}>
                  <ItemLink
                    href={item.disabled ? undefined : item.path}
                    current={current}
                    aria-current={current ? "page" : undefined}
                    aria-disabled={item.disabled || undefined}
                    onClick={event => {
                      if (item.disabled) {
                        event.preventDefault();
                        return;
                      }
                      if (item.onSelect) {
                        event.preventDefault();
                        item.onSelect(item.path);
                      }
                    }}
                  >
                    {item.label}
                  </ItemLink>
                </li>
              );
            })}
        </ItemList>
      )}
    </Group>
  );
};
